import { useState, useEffect } from 'react';
import { TrendingUp, Users, Clock, ArrowDownRight, Timer, CalendarOff, ChevronLeft, ChevronRight, Download, Loader2, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import AdminLayout from '@/components/AdminLayout';
import { adminAPI } from '@/lib/api';
import { formatDate } from '@/lib/dateUtils';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from 'recharts';
import { useBranchFilter } from '@/components/BranchFilterContext';

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const { branch } = useBranchFilter();
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  useEffect(() => {
    fetchAnalytics();
  }, [month, branch]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getAnalytics(month, branch);
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const changeMonth = (dir) => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 1 + dir, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = { month, ...(branch ? { branch } : {}) };
      const res = await adminAPI.exportAnalytics(params);
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attendance_report_${month}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const monthLabel = (() => {
    const [y, m] = month.split('-').map(Number);
    const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    return `${monthNames[m-1]} ${y}`;
  })();

  const chartData = data?.daily?.filter(d => {
    const dt = new Date(d.date);
    return dt <= new Date();
  }).map(d => ({
    ...d,
    day: new Date(d.date).getDate(),
  })) || [];

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 data-testid="analytics-title" className="text-2xl font-extrabold text-[#1a2529] tracking-tight">Monthly Analytics</h1>
            <p className="text-sm text-[#1a2529]/50 mt-0.5">Attendance insights and trends</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="export-excel-btn"
              onClick={handleExport}
              disabled={exporting || loading}
              className="bg-[#026536] hover:bg-[#026536]/90 text-xs h-9 gap-1.5 font-semibold"
            >
              {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              Export Excel
            </Button>
            <div className="flex items-center gap-2 bg-white rounded-xl border border-border/40 px-2 py-1">
            <button data-testid="prev-month" onClick={() => changeMonth(-1)} className="h-8 w-8 rounded-lg flex items-center justify-center hover:bg-[#f4f4f5] transition-colors">
              <ChevronLeft size={16} />
            </button>
            <span data-testid="month-label" className="text-sm font-semibold text-[#1a2529] min-w-[140px] text-center">{monthLabel}</span>
            <button data-testid="next-month" onClick={() => changeMonth(1)} className="h-8 w-8 rounded-lg flex items-center justify-center hover:bg-[#f4f4f5] transition-colors">
              <ChevronRight size={16} />
            </button>
          </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="h-8 w-8 border-2 border-[#026536] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : data ? (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="analytics-summary">
              <SummaryCard icon={TrendingUp} label="Avg Attendance" value={`${data.summary.avg_attendance_pct}%`} color="green" />
              <SummaryCard icon={Users} label="Total Staff" value={data.summary.total_employees} color="blue" />
              <SummaryCard icon={Clock} label="Late Arrivals" value={data.summary.total_late_arrivals} color="amber" />
              <SummaryCard icon={ArrowDownRight} label="Early Departures" value={data.summary.total_early_departures} color="red" />
              <SummaryCard icon={Timer} label="Overtime (hrs)" value={data.summary.total_overtime_hours} color="purple" />
              <SummaryCard icon={Home} label="WFH Days" value={data.summary.total_wfh_days || 0} color="violet" />
              <SummaryCard icon={CalendarOff} label="Total Leaves" value={data.summary.total_leaves} color="gray" />
            </div>

            {/* Daily Attendance Chart */}
            <div className="bg-white rounded-xl p-5 border border-border/40 shadow-sm">
              <h3 className="font-bold text-[#1a2529] mb-4 text-sm">Daily Attendance Trend</h3>
              <div className="h-[260px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                    <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#6b7280' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} />
                    <Tooltip
                      contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}
                      labelFormatter={(v) => `Day ${v}`}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Bar dataKey="present" fill="#026536" radius={[4, 4, 0, 0]} name="Present" />
                    <Bar dataKey="absent" fill="#ef4444" radius={[4, 4, 0, 0]} name="Absent" opacity={0.7} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Late / Early / Overtime Chart */}
            <div className="bg-white rounded-xl p-5 border border-border/40 shadow-sm">
              <h3 className="font-bold text-[#1a2529] mb-4 text-sm">Late Arrivals, Early Departures & Overtime</h3>
              <div className="h-[220px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                    <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#6b7280' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} />
                    <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb' }} labelFormatter={(v) => `Day ${v}`} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Line type="monotone" dataKey="late" stroke="#dea439" strokeWidth={2} dot={{ r: 3 }} name="Late" />
                    <Line type="monotone" dataKey="early" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} name="Early Departure" />
                    <Line type="monotone" dataKey="overtime" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3 }} name="Overtime" />
                    <Line type="monotone" dataKey="wfh" stroke="#7c3aed" strokeWidth={2} dot={{ r: 3 }} name="WFH" strokeDasharray="5 5" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Employee Table */}
            <div className="bg-white rounded-xl border border-border/40 shadow-sm overflow-hidden">
              <div className="p-4 border-b border-border/30">
                <h3 className="font-bold text-[#1a2529] text-sm">Employee-wise Breakdown</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#f4f4f5]/50 text-left">
                      <th className="px-4 py-3 font-semibold text-[#1a2529]/60">Employee</th>
                      <th className="px-3 py-3 font-semibold text-[#1a2529]/60 text-center">Attendance</th>
                      <th className="px-3 py-3 font-semibold text-[#1a2529]/60 text-center hidden sm:table-cell">Present</th>
                      <th className="px-3 py-3 font-semibold text-[#1a2529]/60 text-center hidden sm:table-cell">Leaves</th>
                      <th className="px-3 py-3 font-semibold text-[#1a2529]/60 text-center hidden md:table-cell">Late</th>
                      <th className="px-3 py-3 font-semibold text-[#1a2529]/60 text-center hidden md:table-cell">WFH</th>
                      <th className="px-3 py-3 font-semibold text-[#1a2529]/60 text-center hidden md:table-cell">Early Dep.</th>
                      <th className="px-3 py-3 font-semibold text-[#1a2529]/60 text-center hidden lg:table-cell">OT (min)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.employees.map((emp, idx) => (
                      <tr key={emp.id} data-testid={`analytics-emp-${idx}`} className="border-b border-border/20 hover:bg-[#f4f4f5]/50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            <div className="h-8 w-8 rounded-full bg-[#026536]/10 flex items-center justify-center text-xs font-bold text-[#026536] shrink-0">
                              {emp.name?.[0]?.toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <p className="font-semibold text-[#1a2529] truncate">{emp.name}</p>
                              <p className="text-xs text-[#1a2529]/40 truncate">{emp.phone}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <Badge className={
                            emp.attendance_pct >= 90 ? 'bg-[#026536]/10 text-[#026536] border-0' :
                            emp.attendance_pct >= 70 ? 'bg-[#dea439]/10 text-[#dea439] border-0' :
                            'bg-red-50 text-red-500 border-0'
                          }>
                            {emp.attendance_pct}%
                          </Badge>
                        </td>
                        <td className="px-3 py-3 text-center font-medium text-[#026536] hidden sm:table-cell">{emp.present}/{emp.total_days}</td>
                        <td className="px-3 py-3 text-center font-medium text-[#1a2529]/60 hidden sm:table-cell">{emp.leaves}</td>
                        <td className="px-3 py-3 text-center hidden md:table-cell">
                          {emp.late > 0 ? <span className="text-[#dea439] font-medium">{emp.late}</span> : <span className="text-[#1a2529]/30">0</span>}
                        </td>
                        <td className="px-3 py-3 text-center hidden md:table-cell">
                          {(emp.wfh_days || 0) > 0 ? <span className="text-violet-600 font-medium">{emp.wfh_days}</span> : <span className="text-[#1a2529]/30">0</span>}
                        </td>
                        <td className="px-3 py-3 text-center hidden md:table-cell">
                          {emp.early_departures > 0 ? <span className="text-red-500 font-medium">{emp.early_departures}</span> : <span className="text-[#1a2529]/30">0</span>}
                        </td>
                        <td className="px-3 py-3 text-center hidden lg:table-cell">
                          {emp.overtime_minutes > 0 ? <span className="text-purple-600 font-medium">{emp.overtime_minutes}</span> : <span className="text-[#1a2529]/30">0</span>}
                        </td>
                      </tr>
                    ))}
                    {data.employees.length === 0 && (
                      <tr><td colSpan={8} className="px-4 py-8 text-center text-[#1a2529]/40">No employees found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </AdminLayout>
  );
}

function SummaryCard({ icon: Icon, label, value, color }) {
  const colors = {
    green: 'bg-[#026536]/10 text-[#026536]',
    blue: 'bg-blue-50 text-blue-600',
    amber: 'bg-[#dea439]/10 text-[#dea439]',
    red: 'bg-red-50 text-red-500',
    purple: 'bg-purple-50 text-purple-600',
    violet: 'bg-violet-50 text-violet-600',
    gray: 'bg-gray-100 text-gray-500',
  };
  return (
    <div className="bg-white rounded-xl p-4 border border-border/40 shadow-[0_2px_10px_-2px_rgba(0,0,0,0.05)]">
      <div className={`h-8 w-8 rounded-lg ${colors[color]} flex items-center justify-center mb-2`}>
        <Icon size={16} />
      </div>
      <p className="text-xl font-extrabold text-[#1a2529]">{value}</p>
      <p className="text-[10px] text-[#1a2529]/40 font-semibold mt-0.5 uppercase tracking-wider">{label}</p>
    </div>
  );
}
